import { useState } from 'react';
import { motion } from 'framer-motion';
import {
  ArrowLeftOnRectangleIcon,
  PencilSquareIcon,
  DocumentTextIcon,
  PhotoIcon,
  UserCircleIcon,
  CodeBracketIcon,
  ChartBarIcon,
  Cog6ToothIcon,
  XMarkIcon,
  CheckIcon,
  ArrowPathIcon
} from '@heroicons/react/24/outline';

interface AdminDashboardProps {
  onLogout: () => void;
}

interface ContentSection {
  id: string;
  title: string;
  type: 'text' | 'html' | 'image' | 'list';
  content: string | string[];
  originalContent: string | string[];
  isEditing: boolean;
  component: string;
}

export default function AdminDashboard({ onLogout }: AdminDashboardProps) {
  const [activeTab, setActiveTab] = useState('content');
  const [sections, setSections] = useState<ContentSection[]>([
    {
      id: 'hero-title',
      title: 'Hero Title',
      type: 'text',
      content: 'Full Stack Developer & AI Enthusiast',
      originalContent: 'Full Stack Developer & AI Enthusiast',
      isEditing: false,
      component: 'Hero'
    },
    {
      id: 'hero-description',
      title: 'Hero Description',
      type: 'text',
      content: 'Building innovative web applications with modern technologies',
      originalContent: 'Building innovative web applications with modern technologies',
      isEditing: false,
      component: 'Hero'
    },
    {
      id: 'about-text',
      title: 'About Text',
      type: 'html',
      content: '<p>I am a passionate developer with expertise in React, Node.js, and cloud technologies. With over 5 years of experience building web applications, I focus on creating scalable and user-friendly solutions.</p>',
      originalContent: '<p>I am a passionate developer with expertise in React, Node.js, and cloud technologies. With over 5 years of experience building web applications, I focus on creating scalable and user-friendly solutions.</p>',
      isEditing: false,
      component: 'About'
    },
    {
      id: 'skills-list',
      title: 'Skills List',
      type: 'list',
      content: ['React', 'TypeScript', 'Node.js', 'Python', 'AWS', 'Docker'],
      originalContent: ['React', 'TypeScript', 'Node.js', 'Python', 'AWS', 'Docker'],
      isEditing: false,
      component: 'Skills'
    },
    {
      id: 'project1-title',
      title: 'Project 1 Title',
      type: 'text',
      content: 'Enterprise Application Development',
      originalContent: 'Enterprise Application Development',
      isEditing: false,
      component: 'Projects'
    },
    {
      id: 'project1-description',
      title: 'Project 1 Description',
      type: 'text',
      content: 'Built a scalable enterprise application with React, Node.js, and MongoDB',
      originalContent: 'Built a scalable enterprise application with React, Node.js, and MongoDB',
      isEditing: false,
      component: 'Projects'
    },
    {
      id: 'contact-email',
      title: 'Contact Email',
      type: 'text',
      content: 'contact@example.com',
      originalContent: 'contact@example.com',
      isEditing: false,
      component: 'Contact'
    }
  ]);
  
  const [isSaving, setIsSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState('');
  const [filter, setFilter] = useState('all');

  const startEditing = (id: string) => {
    setSections(sections.map(section => 
      section.id === id ? { ...section, isEditing: true } : section
    ));
  };

  const cancelEditing = (id: string) => {
    setSections(sections.map(section => 
      section.id === id ? { ...section, isEditing: false, content: section.originalContent } : section
    ));
  };

  const saveEditing = (id: string) => {
    setSections(sections.map(section => 
      section.id === id ? { ...section, isEditing: false, originalContent: section.content } : section
    ));
  };

  const handleContentChange = (id: string, value: string | string[]) => {
    setSections(sections.map(section => 
      section.id === id ? { ...section, content: value } : section
    ));
  };

  const handleListItemChange = (sectionId: string, index: number, value: string) => {
    setSections(sections.map(section => {
      if (section.id === sectionId && Array.isArray(section.content)) {
        const newContent = [...section.content];
        newContent[index] = value;
        return { ...section, content: newContent };
      }
      return section;
    }));
  };

  const addListItem = (sectionId: string) => {
    setSections(sections.map(section => {
      if (section.id === sectionId && Array.isArray(section.content)) {
        return { ...section, content: [...section.content, 'New Item'] };
      }
      return section;
    }));
  };

  const removeListItem = (sectionId: string, index: number) => {
    setSections(sections.map(section => {
      if (section.id === sectionId && Array.isArray(section.content)) {
        const newContent = [...section.content];
        newContent.splice(index, 1);
        return { ...section, content: newContent };
      }
      return section;
    }));
  };

  const saveAllChanges = () => {
    setIsSaving(true);
    
    // Simulate API call to save changes
    setTimeout(() => {
      setSections(sections.map(section => ({
        ...section,
        originalContent: section.content
      })));
      setIsSaving(false);
      setSaveMessage('All changes saved successfully!');
      
      setTimeout(() => {
        setSaveMessage('');
      }, 3000);
    }, 1500);
  };

  const filteredSections = filter === 'all' 
    ? sections 
    : sections.filter(section => section.component.toLowerCase() === filter.toLowerCase());

  const hasUnsavedChanges = sections.some(section => {
    if (Array.isArray(section.content) && Array.isArray(section.originalContent)) {
      if (section.content.length !== section.originalContent.length) return true;
      return section.content.some((item, index) => item !== section.originalContent[index]);
    }
    return section.content !== section.originalContent;
  });

  const getNavIcon = (tabName: string) => {
    switch (tabName) {
      case 'content': return <DocumentTextIcon className="w-5 h-5" />;
      case 'media': return <PhotoIcon className="w-5 h-5" />;
      case 'users': return <UserCircleIcon className="w-5 h-5" />;
      case 'code': return <CodeBracketIcon className="w-5 h-5" />;
      case 'analytics': return <ChartBarIcon className="w-5 h-5" />;
      case 'settings': return <Cog6ToothIcon className="w-5 h-5" />;
      default: return <DocumentTextIcon className="w-5 h-5" />;
    }
  };

  return (
    <div className="min-h-screen bg-gray-900 text-white">
      {/* Top Navigation */}
      <div className="bg-gray-800 border-b border-gray-700 px-4 py-3 flex justify-between items-center">
        <div className="flex items-center space-x-3">
          <div className="w-8 h-8 bg-gradient-to-r from-purple-500 to-indigo-500 rounded-md flex items-center justify-center">
            <PencilSquareIcon className="w-5 h-5 text-white" />
          </div>
          <h1 className="text-xl font-bold">Portfolio CMS</h1>
        </div>
        
        <button
          onClick={onLogout}
          className="flex items-center space-x-2 bg-gray-700 hover:bg-gray-600 px-3 py-2 rounded-md transition-colors"
        >
          <ArrowLeftOnRectangleIcon className="w-5 h-5" />
          <span>Logout</span>
        </button>
      </div>

      <div className="flex h-[calc(100vh-57px)]">
        {/* Sidebar */}
        <div className="w-64 bg-gray-800 border-r border-gray-700 flex flex-col">
          <div className="p-4">
            <div className="bg-gray-700/50 rounded-md p-2">
              <div className="text-sm font-medium text-gray-400">Logged in as</div>
              <div className="flex items-center space-x-2 mt-1">
                <div className="w-8 h-8 bg-purple-500 rounded-full flex items-center justify-center">
                  <span className="font-semibold">A</span>
                </div>
                <div>
                  <div className="font-medium">Admin User</div>
                  <div className="text-xs text-gray-400">admin@example.com</div>
                </div>
              </div>
            </div>
          </div>
          
          <nav className="mt-2 flex-1">
            {['content', 'media', 'users', 'code', 'analytics', 'settings'].map((tab) => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`flex items-center space-x-3 px-4 py-3 w-full text-left transition-colors ${activeTab === tab ? 'bg-purple-500/20 border-r-4 border-purple-500 text-purple-400' : 'text-gray-400 hover:bg-gray-700/50 hover:text-white'}`}
              >
                {getNavIcon(tab)}
                <span className="capitalize">{tab}</span>
              </button>
            ))}
          </nav>
          
          <div className="p-4 border-t border-gray-700">
            <div className="text-xs text-gray-500 text-center">
              Portfolio CMS v1.0.0
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="flex-1 overflow-auto">
          {activeTab === 'content' && (
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold">Content Management</h2>
                
                <div className="flex items-center space-x-4">
                  <div className="flex items-center space-x-2">
                    <span className="text-sm text-gray-400">Filter by:</span>
                    <select
                      value={filter}
                      onChange={(e) => setFilter(e.target.value)}
                      className="bg-gray-700 border border-gray-600 rounded-md px-3 py-1.5 text-sm focus:outline-none focus:border-purple-500"
                    >
                      <option value="all">All Components</option>
                      <option value="Hero">Hero</option>
                      <option value="About">About</option>
                      <option value="Skills">Skills</option>
                      <option value="Projects">Projects</option>
                      <option value="Contact">Contact</option>
                    </select>
                  </div>
                  
                  <button
                    onClick={saveAllChanges}
                    disabled={!hasUnsavedChanges || isSaving}
                    className={`flex items-center space-x-2 px-4 py-2 rounded-md transition-colors ${hasUnsavedChanges ? 'bg-purple-600 hover:bg-purple-700' : 'bg-gray-700 cursor-not-allowed text-gray-400'}`}
                  >
                    {isSaving ? (
                      <>
                        <ArrowPathIcon className="w-5 h-5 animate-spin" />
                        <span>Saving...</span>
                      </>
                    ) : (
                      <>
                        <CheckIcon className="w-5 h-5" />
                        <span>Save All Changes</span>
                      </>
                    )}
                  </button>
                </div>
              </div>
              
              {saveMessage && (
                <motion.div
                  initial={{ opacity: 0, y: -20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0 }}
                  className="mb-6 bg-green-500/10 border border-green-500/30 text-green-400 px-4 py-3 rounded-md flex items-center justify-between"
                >
                  <div className="flex items-center space-x-2">
                    <CheckIcon className="w-5 h-5" />
                    <span>{saveMessage}</span>
                  </div>
                </motion.div>
              )}
              
              <div className="grid grid-cols-1 gap-6">
                {filteredSections.map((section) => (
                  <div 
                    key={section.id}
                    className="bg-gray-800 border border-gray-700 rounded-lg overflow-hidden"
                  >
                    <div className="flex justify-between items-center px-4 py-3 bg-gray-800 border-b border-gray-700">
                      <div>
                        <h3 className="font-medium">{section.title}</h3>
                        <div className="text-xs text-gray-400">Component: {section.component}</div>
                      </div>
                      
                      {section.isEditing ? (
                        <div className="flex items-center space-x-2">
                          <button
                            onClick={() => cancelEditing(section.id)}
                            className="p-1.5 bg-gray-700 hover:bg-gray-600 rounded-md transition-colors"
                          >
                            <XMarkIcon className="w-5 h-5 text-gray-400" />
                          </button>
                          <button
                            onClick={() => saveEditing(section.id)}
                            className="p-1.5 bg-purple-500/20 hover:bg-purple-500/30 text-purple-400 rounded-md transition-colors"
                          >
                            <CheckIcon className="w-5 h-5" />
                          </button>
                        </div>
                      ) : (
                        <button
                          onClick={() => startEditing(section.id)}
                          className="flex items-center space-x-1.5 px-3 py-1.5 bg-gray-700 hover:bg-gray-600 rounded-md transition-colors"
                        >
                          <PencilSquareIcon className="w-4 h-4" />
                          <span>Edit</span>
                        </button>
                      )}
                    </div>
                    
                    <div className="p-4">
                      {section.isEditing ? (
                        <div>
                          {section.type === 'text' && (
                            <input
                              type="text"
                              value={section.content as string}
                              onChange={(e) => handleContentChange(section.id, e.target.value)}
                              className="w-full bg-gray-700 border border-gray-600 rounded-md px-3 py-2 focus:outline-none focus:border-purple-500"
                            />
                          )}
                          
                          {section.type === 'html' && (
                            <textarea
                              value={section.content as string}
                              onChange={(e) => handleContentChange(section.id, e.target.value)}
                              rows={5}
                              className="w-full bg-gray-700 border border-gray-600 rounded-md px-3 py-2 focus:outline-none focus:border-purple-500"
                            />
                          )}
                          
                          {section.type === 'list' && Array.isArray(section.content) && (
                            <div className="space-y-2">
                              {section.content.map((item, index) => (
                                <div key={index} className="flex items-center space-x-2">
                                  <input
                                    type="text"
                                    value={item}
                                    onChange={(e) => handleListItemChange(section.id, index, e.target.value)}
                                    className="flex-1 bg-gray-700 border border-gray-600 rounded-md px-3 py-2 focus:outline-none focus:border-purple-500"
                                  />
                                  <button
                                    onClick={() => removeListItem(section.id, index)}
                                    className="p-2 bg-red-500/20 hover:bg-red-500/30 text-red-400 rounded-md transition-colors"
                                  >
                                    <XMarkIcon className="w-4 h-4" />
                                  </button>
                                </div>
                              ))}
                              
                              <button
                                onClick={() => addListItem(section.id)}
                                className="mt-2 flex items-center space-x-1.5 px-3 py-1.5 bg-gray-700 hover:bg-gray-600 rounded-md transition-colors text-sm"
                              >
                                <span>+ Add Item</span>
                              </button>
                            </div>
                          )}
                        </div>
                      ) : (
                        <div>
                          {section.type === 'text' && (
                            <div className="bg-gray-700/50 rounded-md px-3 py-2">
                              {section.content as string}
                            </div>
                          )}
                          
                          {section.type === 'html' && (
                            <div className="bg-gray-700/50 rounded-md px-3 py-2 whitespace-pre-wrap font-mono text-sm">
                              {section.content as string}
                            </div>
                          )}
                          
                          {section.type === 'list' && Array.isArray(section.content) && (
                            <div className="bg-gray-700/50 rounded-md px-3 py-2">
                              <ul className="list-disc list-inside space-y-1">
                                {section.content.map((item, index) => (
                                  <li key={index}>{item}</li>
                                ))}
                              </ul>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
          
          {/* Media Management */}
          {activeTab === 'media' && (
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold">Media Library</h2>
                <button className="flex items-center space-x-2 bg-purple-600 hover:bg-purple-700 px-4 py-2 rounded-md transition-colors">
                  <PhotoIcon className="w-5 h-5" />
                  <span>Upload Media</span>
                </button>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                {[
                  { name: 'hero-background.jpg', size: '2.4 MB', type: 'image', uploaded: '2024-01-15' },
                  { name: 'project-screenshot-1.png', size: '1.8 MB', type: 'image', uploaded: '2024-01-14' },
                  { name: 'resume.pdf', size: '245 KB', type: 'document', uploaded: '2024-01-13' },
                  { name: 'demo-video.mp4', size: '15.2 MB', type: 'video', uploaded: '2024-01-12' },
                  { name: 'logo.svg', size: '12 KB', type: 'image', uploaded: '2024-01-11' },
                  { name: 'portfolio-assets.zip', size: '5.7 MB', type: 'archive', uploaded: '2024-01-10' }
                ].map((file, index) => (
                  <div key={index} className="bg-gray-800 border border-gray-700 rounded-lg p-4 hover:border-purple-500 transition-colors cursor-pointer">
                    <div className="flex items-center justify-between mb-2">
                      <PhotoIcon className="w-8 h-8 text-purple-400" />
                      <span className="text-xs text-gray-400">{file.type}</span>
                    </div>
                    <h3 className="font-medium text-sm mb-1 truncate">{file.name}</h3>
                    <div className="text-xs text-gray-400">
                      <div>{file.size}</div>
                      <div>{file.uploaded}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
          
          {/* Users Management */}
          {activeTab === 'users' && (
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold">User Management</h2>
                <button className="flex items-center space-x-2 bg-purple-600 hover:bg-purple-700 px-4 py-2 rounded-md transition-colors">
                  <UserCircleIcon className="w-5 h-5" />
                  <span>Add User</span>
                </button>
              </div>
              
              <div className="bg-gray-800 border border-gray-700 rounded-lg overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-700">
                      <tr>
                        <th className="px-4 py-3 text-left text-sm font-medium text-gray-300">User</th>
                        <th className="px-4 py-3 text-left text-sm font-medium text-gray-300">Role</th>
                        <th className="px-4 py-3 text-left text-sm font-medium text-gray-300">Status</th>
                        <th className="px-4 py-3 text-left text-sm font-medium text-gray-300">Last Login</th>
                        <th className="px-4 py-3 text-left text-sm font-medium text-gray-300">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-700">
                      {[
                        { name: 'Admin User', email: 'admin@example.com', role: 'Administrator', status: 'Active', lastLogin: '2024-01-15 14:30' },
                        { name: 'John Doe', email: 'john@example.com', role: 'Editor', status: 'Active', lastLogin: '2024-01-14 09:15' },
                        { name: 'Jane Smith', email: 'jane@example.com', role: 'Contributor', status: 'Inactive', lastLogin: '2024-01-10 16:45' },
                        { name: 'Mike Johnson', email: 'mike@example.com', role: 'Viewer', status: 'Active', lastLogin: '2024-01-13 11:20' }
                      ].map((user, index) => (
                        <tr key={index} className="hover:bg-gray-700/50">
                          <td className="px-4 py-3">
                            <div className="flex items-center space-x-3">
                              <div className="w-8 h-8 bg-purple-500 rounded-full flex items-center justify-center">
                                <span className="font-semibold text-sm">{user.name.charAt(0)}</span>
                              </div>
                              <div>
                                <div className="font-medium">{user.name}</div>
                                <div className="text-sm text-gray-400">{user.email}</div>
                              </div>
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                              user.role === 'Administrator' ? 'bg-red-500/20 text-red-400' :
                              user.role === 'Editor' ? 'bg-blue-500/20 text-blue-400' :
                              user.role === 'Contributor' ? 'bg-green-500/20 text-green-400' :
                              'bg-gray-500/20 text-gray-400'
                            }`}>
                              {user.role}
                            </span>
                          </td>
                          <td className="px-4 py-3">
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                              user.status === 'Active' ? 'bg-green-500/20 text-green-400' : 'bg-gray-500/20 text-gray-400'
                            }`}>
                              {user.status}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-400">{user.lastLogin}</td>
                          <td className="px-4 py-3">
                            <div className="flex items-center space-x-2">
                              <button className="p-1 bg-gray-700 hover:bg-gray-600 rounded transition-colors">
                                <PencilSquareIcon className="w-4 h-4" />
                              </button>
                              <button className="p-1 bg-red-500/20 hover:bg-red-500/30 text-red-400 rounded transition-colors">
                                <XMarkIcon className="w-4 h-4" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}
          
          {/* Code Editor */}
          {activeTab === 'code' && (
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold">Code Editor</h2>
                <div className="flex items-center space-x-2">
                  <select className="bg-gray-700 border border-gray-600 rounded-md px-3 py-1.5 text-sm focus:outline-none focus:border-purple-500">
                    <option>CSS Styles</option>
                    <option>JavaScript</option>
                    <option>HTML Templates</option>
                    <option>Configuration</option>
                  </select>
                  <button className="flex items-center space-x-2 bg-purple-600 hover:bg-purple-700 px-4 py-2 rounded-md transition-colors">
                    <CheckIcon className="w-5 h-5" />
                    <span>Save Changes</span>
                  </button>
                </div>
              </div>
              
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                <div className="lg:col-span-2">
                  <div className="bg-gray-800 border border-gray-700 rounded-lg overflow-hidden">
                    <div className="bg-gray-700 px-4 py-2 border-b border-gray-600">
                      <span className="text-sm font-medium">styles.css</span>
                    </div>
                    <div className="p-4">
                      <textarea
                        className="w-full h-96 bg-gray-900 border border-gray-600 rounded-md p-4 font-mono text-sm focus:outline-none focus:border-purple-500 resize-none"
                        defaultValue={`/* Custom Portfolio Styles */
.hero-section {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
}

.card-hover {
  transition: transform 0.3s ease, box-shadow 0.3s ease;
}

.card-hover:hover {
  transform: translateY(-5px);
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.3);
}

.gradient-text {
  background: linear-gradient(45deg, #667eea, #764ba2);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

@media (max-width: 768px) {
  .hero-section {
    padding: 2rem 1rem;
  }
}`}
                      />
                    </div>
                  </div>
                </div>
                
                <div>
                  <div className="bg-gray-800 border border-gray-700 rounded-lg p-4">
                    <h3 className="font-semibold mb-4">File Explorer</h3>
                    <div className="space-y-2 text-sm">
                      {[
                        { name: 'styles.css', type: 'file', active: true },
                        { name: 'main.js', type: 'file' },
                        { name: 'components/', type: 'folder' },
                        { name: '├── Header.tsx', type: 'file', indent: true },
                        { name: '├── Footer.tsx', type: 'file', indent: true },
                        { name: '└── Layout.tsx', type: 'file', indent: true },
                        { name: 'utils/', type: 'folder' },
                        { name: '├── helpers.js', type: 'file', indent: true },
                        { name: '└── constants.js', type: 'file', indent: true }
                      ].map((item, index) => (
                        <div key={index} className={`flex items-center space-x-2 p-2 rounded cursor-pointer transition-colors ${
                          item.active ? 'bg-purple-500/20 text-purple-400' : 'hover:bg-gray-700/50'
                        } ${item.indent ? 'ml-4' : ''}`}>
                          {item.type === 'folder' ? (
                            <span className="text-yellow-400">📁</span>
                          ) : (
                            <span className="text-blue-400">📄</span>
                          )}
                          <span>{item.name}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
          
          {/* Analytics */}
          {activeTab === 'analytics' && (
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold">Analytics Dashboard</h2>
                <div className="flex items-center space-x-2">
                  <select className="bg-gray-700 border border-gray-600 rounded-md px-3 py-1.5 text-sm focus:outline-none focus:border-purple-500">
                    <option>Last 7 days</option>
                    <option>Last 30 days</option>
                    <option>Last 3 months</option>
                    <option>Last year</option>
                  </select>
                </div>
              </div>
              
              {/* Stats Cards */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
                {[
                  { title: 'Total Visitors', value: '12,847', change: '+12.5%', positive: true },
                  { title: 'Page Views', value: '28,392', change: '+8.2%', positive: true },
                  { title: 'Bounce Rate', value: '34.2%', change: '-2.1%', positive: true },
                  { title: 'Avg. Session', value: '3m 42s', change: '+15.3%', positive: true }
                ].map((stat, index) => (
                  <div key={index} className="bg-gray-800 border border-gray-700 rounded-lg p-6">
                    <div className="flex items-center justify-between mb-2">
                      <h3 className="text-sm font-medium text-gray-400">{stat.title}</h3>
                      <ChartBarIcon className="w-5 h-5 text-purple-400" />
                    </div>
                    <div className="text-2xl font-bold mb-1">{stat.value}</div>
                    <div className={`text-sm flex items-center space-x-1 ${
                      stat.positive ? 'text-green-400' : 'text-red-400'
                    }`}>
                      <span>{stat.change}</span>
                      <span className="text-gray-400">vs last period</span>
                    </div>
                  </div>
                ))}
              </div>
              
              {/* Charts */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="bg-gray-800 border border-gray-700 rounded-lg p-6">
                  <h3 className="text-lg font-semibold mb-4">Traffic Sources</h3>
                  <div className="space-y-4">
                    {[
                      { source: 'Direct', visitors: 4521, percentage: 35.2 },
                      { source: 'Google Search', visitors: 3847, percentage: 29.9 },
                      { source: 'Social Media', visitors: 2156, percentage: 16.8 },
                      { source: 'Referrals', visitors: 1523, percentage: 11.9 },
                      { source: 'Email', visitors: 800, percentage: 6.2 }
                    ].map((item, index) => (
                      <div key={index} className="flex items-center justify-between">
                        <div className="flex items-center space-x-3">
                          <div className="w-3 h-3 bg-purple-500 rounded-full" style={{
                            backgroundColor: `hsl(${index * 60}, 70%, 60%)`
                          }}></div>
                          <span className="text-sm">{item.source}</span>
                        </div>
                        <div className="flex items-center space-x-4">
                          <span className="text-sm text-gray-400">{item.visitors.toLocaleString()}</span>
                          <span className="text-sm font-medium">{item.percentage}%</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
                
                <div className="bg-gray-800 border border-gray-700 rounded-lg p-6">
                  <h3 className="text-lg font-semibold mb-4">Popular Pages</h3>
                  <div className="space-y-4">
                    {[
                      { page: '/', title: 'Home Page', views: 8934, time: '4m 12s' },
                      { page: '/projects', title: 'Projects', views: 5621, time: '6m 45s' },
                      { page: '/about', title: 'About Me', views: 3847, time: '3m 28s' },
                      { page: '/contact', title: 'Contact', views: 2156, time: '2m 15s' },
                      { page: '/playground', title: 'Code Playground', views: 1523, time: '8m 32s' }
                    ].map((page, index) => (
                      <div key={index} className="flex items-center justify-between py-2">
                        <div>
                          <div className="font-medium text-sm">{page.title}</div>
                          <div className="text-xs text-gray-400">{page.page}</div>
                        </div>
                        <div className="text-right">
                          <div className="text-sm font-medium">{page.views.toLocaleString()}</div>
                          <div className="text-xs text-gray-400">{page.time}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}
          
          {/* Settings */}
          {activeTab === 'settings' && (
            <div className="p-6">
              <h2 className="text-2xl font-bold mb-6">System Settings</h2>
              
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* General Settings */}
                <div className="bg-gray-800 border border-gray-700 rounded-lg p-6">
                  <h3 className="text-lg font-semibold mb-4">General Settings</h3>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">Site Title</label>
                      <input
                        type="text"
                        defaultValue="Harsh Trivedi - Portfolio"
                        className="w-full bg-gray-700 border border-gray-600 rounded-md px-3 py-2 focus:outline-none focus:border-purple-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">Site Description</label>
                      <textarea
                        defaultValue="Full Stack Developer & AI Enthusiast building innovative web applications"
                        className="w-full bg-gray-700 border border-gray-600 rounded-md px-3 py-2 focus:outline-none focus:border-purple-500"
                        rows={3}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">Contact Email</label>
                      <input
                        type="email"
                        defaultValue="contact@example.com"
                        className="w-full bg-gray-700 border border-gray-600 rounded-md px-3 py-2 focus:outline-none focus:border-purple-500"
                      />
                    </div>
                  </div>
                </div>
                
                {/* Security Settings */}
                <div className="bg-gray-800 border border-gray-700 rounded-lg p-6">
                  <h3 className="text-lg font-semibold mb-4">Security Settings</h3>
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="font-medium">Two-Factor Authentication</div>
                        <div className="text-sm text-gray-400">Add an extra layer of security</div>
                      </div>
                      <button className="bg-purple-600 hover:bg-purple-700 px-3 py-1 rounded text-sm transition-colors">
                        Enable
                      </button>
                    </div>
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="font-medium">Login Notifications</div>
                        <div className="text-sm text-gray-400">Get notified of new logins</div>
                      </div>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input type="checkbox" className="sr-only peer" defaultChecked />
                        <div className="w-11 h-6 bg-gray-600 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-purple-600"></div>
                      </label>
                    </div>
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="font-medium">Session Timeout</div>
                        <div className="text-sm text-gray-400">Auto logout after inactivity</div>
                      </div>
                      <select className="bg-gray-700 border border-gray-600 rounded-md px-3 py-1 text-sm focus:outline-none focus:border-purple-500">
                        <option>30 minutes</option>
                        <option>1 hour</option>
                        <option>4 hours</option>
                        <option>Never</option>
                      </select>
                    </div>
                  </div>
                </div>
                
                {/* Performance Settings */}
                <div className="bg-gray-800 border border-gray-700 rounded-lg p-6">
                  <h3 className="text-lg font-semibold mb-4">Performance</h3>
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="font-medium">Enable Caching</div>
                        <div className="text-sm text-gray-400">Improve site loading speed</div>
                      </div>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input type="checkbox" className="sr-only peer" defaultChecked />
                        <div className="w-11 h-6 bg-gray-600 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-purple-600"></div>
                      </label>
                    </div>
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="font-medium">Image Optimization</div>
                        <div className="text-sm text-gray-400">Automatically optimize images</div>
                      </div>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input type="checkbox" className="sr-only peer" defaultChecked />
                        <div className="w-11 h-6 bg-gray-600 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-purple-600"></div>
                      </label>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">Cache Duration</label>
                      <select className="w-full bg-gray-700 border border-gray-600 rounded-md px-3 py-2 focus:outline-none focus:border-purple-500">
                        <option>1 hour</option>
                        <option>6 hours</option>
                        <option>24 hours</option>
                        <option>1 week</option>
                      </select>
                    </div>
                  </div>
                </div>
                
                {/* Backup Settings */}
                <div className="bg-gray-800 border border-gray-700 rounded-lg p-6">
                  <h3 className="text-lg font-semibold mb-4">Backup & Recovery</h3>
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="font-medium">Automatic Backups</div>
                        <div className="text-sm text-gray-400">Last backup: 2 hours ago</div>
                      </div>
                      <button className="bg-green-600 hover:bg-green-700 px-3 py-1 rounded text-sm transition-colors">
                        Backup Now
                      </button>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">Backup Frequency</label>
                      <select className="w-full bg-gray-700 border border-gray-600 rounded-md px-3 py-2 focus:outline-none focus:border-purple-500">
                        <option>Daily</option>
                        <option>Weekly</option>
                        <option>Monthly</option>
                        <option>Manual only</option>
                      </select>
                    </div>
                    <div className="bg-blue-500/10 border border-blue-400/20 rounded-lg p-3">
                      <div className="text-sm text-blue-300">
                        <strong>Storage Used:</strong> 2.4 GB / 10 GB<br/>
                        <strong>Available Backups:</strong> 15 backups
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="mt-6 flex justify-end">
                <button className="flex items-center space-x-2 bg-purple-600 hover:bg-purple-700 px-6 py-2 rounded-md transition-colors">
                  <CheckIcon className="w-5 h-5" />
                  <span>Save All Settings</span>
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}